import { platformSearch } from 'api/platformRequest';
import { formatNumberAsRepresentation, getProductValue } from 'helpers';
import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min';
import Button from 'ReusableComponents/Button';
import WhiteStartSearchModal from 'asset/icon/filled/white-start-search-modal.svg';
import {
  clearPlatformSearchInputValue,
  storePlatformSearchInputValue,
} from 'store/platform/actions';
import { DESIGN_CONFIG, STRING_CONFIG } from 'utils';
import { LuSearch } from 'react-icons/lu';
import Box from 'ReusableComponents/Box';
import Text from 'ReusableComponents/Text';
import AppIcon from 'asset/icons/AppIcon';
import { rdAssetIcons } from 'asset/icons';

export const fetchSearchResults = async (
  inputValue,
  searchFn,
  onSuccess,
  onError,
  onLoading
) => {
  const trimmedInput = inputValue.trim();

  if (trimmedInput.length < 3) {
    onSuccess([]);
    return;
  }

  onLoading(true);

  try {
    const { data } = await searchFn(trimmedInput);
    if (data?.success) {
      onSuccess(data?.products || []);
    } else {
      onSuccess([]);
    }
  } catch (error) {
    onError(error);
    onSuccess([]);
  } finally {
    onLoading(false);
  }
};

function SearchInput() {
  const dispatch = useDispatch();

  const [isLoading, setIsLoading] = useState(false);

  const inputValueFromStore = useSelector(
    (state) => state.platform.platformSearchInputValue
  );

  const history = useHistory();

  const [suggestions, setSuggestions] = useState([]);

  const [isFocused, setIsFocused] = useState(false);

  const [selectedSuggestionIndex, setSelectedSuggestionIndex] = useState(-1);

  useEffect(() => {
    fetchSearchResults(
      inputValueFromStore,
      platformSearch,
      setSuggestions,
      (error) => console.error(error),
      setIsLoading
    );
  }, [inputValueFromStore]);

  const handleKeyDown = (event) => {
    const totalVisibleSuggestions = Math.min(5, suggestions.length);

    const totalItems = totalVisibleSuggestions + 1;
    if (event.key === 'ArrowDown') {
      event.preventDefault();

      setSelectedSuggestionIndex((prevIndex) => (prevIndex + 1) % totalItems);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();

      setSelectedSuggestionIndex(
        (prevIndex) => (prevIndex - 1 + totalItems) % totalItems
      );
    } else if (event.key === 'Enter') {
      event.preventDefault();
      if (
        selectedSuggestionIndex >= 0 &&
        selectedSuggestionIndex < totalVisibleSuggestions
      ) {
        const selectedProduct = suggestions[selectedSuggestionIndex];

        history.push(
          selectedProduct?.url_slug
            ? `/product/${selectedProduct.code}/${selectedProduct.url_slug}`
            : `/product/${selectedProduct.code}`
        );
      } else if (selectedSuggestionIndex === totalVisibleSuggestions) {
        history.push(`/search/${inputValueFromStore}`);
      } else if (inputValueFromStore.trim()) {
        history.push(`/search/${inputValueFromStore}`);
      }
    }
  };

  useEffect(() => {
    setSelectedSuggestionIndex(-1);
  }, [suggestions]);

  useEffect(() => {
    if (!history.location.pathname.includes('/search')) {
      dispatch(clearPlatformSearchInputValue(''));
    }
  }, [history.location]);

  return (
    <div style={{ position: 'relative' }}>
      <div
        className={`search-input ${isFocused ? 'focused' : null}`}
        role='search'
      >
        <input
          aria-autocomplete='list'
          aria-label='Search Input'
          aria-owns={suggestions?.length > 0 ? 'suggestions-list' : null}
          onBlur={() => setIsFocused(false)}
          onChange={(e) => {
            dispatch(storePlatformSearchInputValue(e.target.value));
          }}
          onFocus={() => setIsFocused(true)}
          onKeyDown={handleKeyDown}
          placeholder={STRING_CONFIG.inPage.platform.plHNavBarSearch}
          type='text'
          value={`${inputValueFromStore}`}
        />
        <AppIcon
          color={
            isFocused
              ? DESIGN_CONFIG.color.primary.primaryMain
              : DESIGN_CONFIG.color.text.text75
          }
          name={rdAssetIcons.search}
          size={18}
        />
      </div>
      {isFocused &&
        inputValueFromStore.length >= 3 &&
        suggestions?.length === 0 &&
        !isLoading && (
          <ul className='suggestions'>
            <Box display='flex' justifyContent='center'>
              <Box
                alignItems='center'
                display='flex'
                flexDirection='column'
                gap='.5rem'
                margin='2.5rem 0'
              >
                <LuSearch color={DESIGN_CONFIG.color.text.text500} size={24} />
                <Text
                  color={DESIGN_CONFIG.color.text.text500}
                  fontSize={
                    DESIGN_CONFIG.typography.section.webSectionTitle.fontSize
                  }
                  fontWeight={
                    DESIGN_CONFIG.typography.section.webSectionTitle.fontWeight
                  }
                  m='0'
                >
                  {`${STRING_CONFIG.inPage.platformSearch.txtNoResultsFor} '${inputValueFromStore}' ${STRING_CONFIG.inPage.platformSearch.txtOtherKeyword}`}
                </Text>
              </Box>
            </Box>
          </ul>
        )}
      {isFocused && inputValueFromStore && suggestions?.length > 0 && (
        <ul className='suggestions' id='suggestions-list' role='listbox'>
          {suggestions.slice(0, 5).map((result, index) => (
            <li
              aria-selected={
                selectedSuggestionIndex === index ? 'true' : 'false'
              }
              className={`list-item ${selectedSuggestionIndex === index ? 'selected' : null}`}
              key={result?.code}
              onMouseDown={(event) => {
                event.preventDefault();
                history.push(
                  result?.url_slug
                    ? `/product/${result.code}/${result.url_slug}`
                    : `/product/${result.code}`
                );
              }}
              role='option'
            >
              <img alt='' className='image' src={result?.image} />
              <div className='product-details'>
                <p
                  style={{
                    color: `${DESIGN_CONFIG.color.text.text500}`,
                    fontSize: `${DESIGN_CONFIG.typography.mainCard.webMnCardTitle.fontSize}`,
                    fontWeight: `${DESIGN_CONFIG.typography.mainCard.webMnCardTitle.fontWeight}`,
                    margin: 0,
                  }}
                >
                  {result?.model} {result?.variant}
                </p>
                <p
                  style={{
                    color: `${DESIGN_CONFIG.color.text.text100}`,
                    fontSize: `${DESIGN_CONFIG.typography.mainCard.webMnCardSubtitle.fontSize}`,
                    fontWeight: `${DESIGN_CONFIG.typography.mainCard.webMnCardSubtitle.fontWeight}`,
                    margin: 0,
                  }}
                >
                  {result?.brand}
                </p>
                <div
                  style={{
                    alignItems: 'center',
                    display: 'flex',
                    gap: '.5rem',
                  }}
                >
                  <div
                    style={{
                      background: `${DESIGN_CONFIG.color.primary.primaryMain}`,
                      display: 'flex',
                      gap: '.25rem',
                      padding: '.25rem',
                    }}
                  >
                    <img
                      alt=''
                      height={20}
                      src={WhiteStartSearchModal}
                      width={12}
                    />
                    <p
                      style={{
                        color: `${DESIGN_CONFIG.color.neutral.neutral500}`,
                        fontSize: `${DESIGN_CONFIG.typography.common.webTag.fontSize}`,
                        fontWeight: `${DESIGN_CONFIG.typography.common.webTag.fontWeight}`,
                        margin: 0,
                      }}
                    >
                      {getProductValue(result?.value, result?.total_reviews)}
                    </p>
                  </div>
                  <p
                    style={{
                      color: `${DESIGN_CONFIG.color.text.text100}`,
                      fontSize: `${DESIGN_CONFIG.typography.mainCard.webMnCardSubtitle.fontSize}`,
                      fontWeight: `${DESIGN_CONFIG.typography.mainCard.webMnCardSubtitle.fontWeight}`,
                      margin: 0,
                    }}
                  >
                    {formatNumberAsRepresentation(result?.total_reviews)}
                  </p>
                </div>
              </div>
            </li>
          ))}
          {suggestions.length > 5 && (
            <Button
              aria-selected={
                selectedSuggestionIndex === suggestions.length
                  ? 'true'
                  : 'false'
              }
              background={DESIGN_CONFIG.color.neutral.neutral500}
              border={`1px solid ${DESIGN_CONFIG.color.neutral.neutral100}`}
              borderRadius='4px'
              cursor='pointer'
              fontSize={DESIGN_CONFIG.typography.common.webButton.fontSize}
              fontWeight={DESIGN_CONFIG.typography.common.webButton.fontWeight}
              marginTop='.5rem'
              onMouseDown={(event) => {
                event.preventDefault();

                history.push(`/search/${inputValueFromStore}`);
              }}
              padding='1rem'
              textColor={DESIGN_CONFIG.color.text.text500}
              w='100%'
            >
              {STRING_CONFIG.inPage.platform.btnViewAllSearchResults}
            </Button>
          )}
        </ul>
      )}
    </div>
  );
}

export default SearchInput;
